/**
 * Hook personnalisé pour la gestion des raisons d'annulation
 * Gère suggestions contextuelles, validation et analytics
 */

import { useState, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  CANCELLATION_REASONS, 
  getReasonCategory, 
  REASON_CATEGORIES,
  VALIDATION_RULES 
} from '../constants/cancellationReasons';

export const useCancellationReasons = (context, userRole) => {
  const { t } = useTranslation('cancellationReasons');
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [errors, setErrors] = useState([]);
  
  // 🎯 CORRECTION: Stabiliser context avec useRef pour éviter re-rendus
  const stableContext = useRef(context);
  const stableUserRole = useRef(userRole);
  
  // Mise à jour silencieuse des refs sans déclencher re-rendu
  stableContext.current = context;
  stableUserRole.current = userRole;

  // Obtenir les raisons suggérées selon le contexte - UNIQUEMENT dépend de context stable
  const suggestedReasons = useMemo(() => {
    const contextKey = stableContext.current;
    const reasons = CANCELLATION_REASONS[contextKey] || [];
    
    return reasons.map(reason => ({
      key: reason,
      label: t(`${contextKey}.${reason}`),
      category: getReasonCategory(reason)
    }));
  }, [context, t]); // Supprimé userRole des dépendances

  // Validation de la raison
  const validateReason = useCallback(() => {
    const newErrors = [];
    const finalReason = selectedReason === 'OTHER' ? customReason : selectedReason;
    
    if (!finalReason || finalReason.trim().length === 0) {
      newErrors.push(t('validation.reasonRequired'));
    }
    
    if (finalReason && finalReason.length < VALIDATION_RULES.MIN_REASON_LENGTH) {
      newErrors.push(t('validation.reasonMinLength', { min: VALIDATION_RULES.MIN_REASON_LENGTH }));
    }
    
    if (finalReason && finalReason.length > VALIDATION_RULES.MAX_REASON_LENGTH) {
      newErrors.push(t('validation.reasonMaxLength', { max: VALIDATION_RULES.MAX_REASON_LENGTH }));
    }
    
    setErrors(newErrors);
    return newErrors.length === 0;
  }, [selectedReason, customReason, t]);

  // Obtenir la raison finale formatée - STABLE
  const getFinalReason = useCallback(() => {
    if (selectedReason === 'OTHER') {
      return customReason.trim();
    }
    if (selectedReason) {
      const contextKey = stableContext.current;
      return t(`${contextKey}.${selectedReason}`);
    }
    return customReason.trim();
  }, [selectedReason, customReason, t]); // Supprimé context des dépendances

  // Obtenir les données d'analyse - STABLE  
  const getAnalyticsData = useCallback(() => ({
    reason_key: selectedReason || 'CUSTOM',
    reason_text: getFinalReason(),
    category: selectedReason ? getReasonCategory(selectedReason) : REASON_CATEGORIES.OTHER,
    context: stableContext.current,
    user_role: stableUserRole.current,
    is_predefined: Boolean(selectedReason && selectedReason !== 'OTHER'),
    is_custom: Boolean(selectedReason === 'OTHER' || !selectedReason),
    character_count: getFinalReason().length,
    selection_timestamp: new Date().toISOString()
  }), [selectedReason, getFinalReason]); // Supprimé context et userRole des dépendances

  // Reset des valeurs
  const resetReason = useCallback(() => {
    setSelectedReason('');
    setCustomReason('');
    setErrors([]);
  }, []);

  // Handlers pour les changements - SANS LOGS pour éviter re-rendus
  const handlePredefinedSelect = useCallback((reason) => {
    setSelectedReason(reason);
    if (reason !== 'OTHER') {
      setCustomReason('');
    }
    // Validation immédiate et stable
    const newErrors = [];
    const finalReason = reason === 'OTHER' ? customReason : reason;
    
    if (!finalReason || finalReason.trim().length === 0) {
      newErrors.push(t('validation.reasonRequired'));
    }
    setErrors(newErrors);
  }, [customReason, t]);

  const handleCustomChange = useCallback((value) => {
    setCustomReason(value);
    // Validation immédiate et stable
    const newErrors = [];
    if (value && value.length < VALIDATION_RULES.MIN_REASON_LENGTH) {
      newErrors.push(t('validation.reasonMinLength', { min: VALIDATION_RULES.MIN_REASON_LENGTH }));
    }
    setErrors(newErrors);
  }, [t]);

  // Statut de validation - STABLE et optimisé
  const isValid = useMemo(() => {
    const finalReason = selectedReason === 'OTHER' ? customReason.trim() : (selectedReason || customReason.trim());
    const isReasonPresent = finalReason && finalReason.length > 0;
    const isLengthValid = finalReason.length >= VALIDATION_RULES.MIN_REASON_LENGTH &&
                         finalReason.length <= VALIDATION_RULES.MAX_REASON_LENGTH;
    return isReasonPresent && isLengthValid;
  }, [selectedReason, customReason]); // Dépendances minimales

  return {
    // États
    suggestedReasons,
    selectedReason,
    customReason,
    errors,
    isValid,
    
    // Setters
    setSelectedReason: handlePredefinedSelect,
    setCustomReason: handleCustomChange,
    
    // Méthodes
    validateReason,
    getFinalReason,
    getAnalyticsData,
    resetReason,
    
    // Handlers
    handlePredefinedSelect,
    handleCustomChange
  };
};

export default useCancellationReasons;
